import React, { useState } from 'react';
import { Trash2, Edit, ArrowRight, AlertTriangle, TrendingUp, Zap, Target, Calendar } from 'lucide-react';
import { GoalDependency } from '../../types';
import { apiService } from '../../services/api';

interface DependencyListViewProps {
  dependencies: GoalDependency[];
  onUpdate: () => void;
  onDelete: () => void;
}

export const DependencyListView: React.FC<DependencyListViewProps> = ({
  dependencies,
  onUpdate,
  onDelete
}) => {
  const [loading, setLoading] = useState<string | null>(null);
  const [editingDependency, setEditingDependency] = useState<GoalDependency | null>(null);
  const [editForm, setEditForm] = useState<{
    dependencyType: 'blocks' | 'enables' | 'reduces_complexity' | 'accelerates';
    strength: number;
    description: string;
    impactPercentage: number;
  }>({
    dependencyType: 'blocks',
    strength: 3,
    description: '',
    impactPercentage: 50
  });

  const getDependencyIcon = (type: string) => {
    switch (type) {
      case 'blocks':
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case 'enables':
        return <TrendingUp className="w-4 h-4 text-green-500" />;
      case 'reduces_complexity':
        return <Target className="w-4 h-4 text-yellow-500" />;
      case 'accelerates':
        return <Zap className="w-4 h-4 text-blue-500" />;
      default:
        return <ArrowRight className="w-4 h-4 text-gray-500" />;
    }
  };

  const getDependencyColor = (type: string) => {
    switch (type) {
      case 'blocks':
        return 'border-red-200 bg-red-50';
      case 'enables':
        return 'border-green-200 bg-green-50';
      case 'reduces_complexity':
        return 'border-yellow-200 bg-yellow-50';
      case 'accelerates':
        return 'border-blue-200 bg-blue-50';
      default:
        return 'border-gray-200 bg-gray-50';
    }
  };

  const formatDependencyType = (type: string) => {
    return type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getStrengthText = (strength: number) => {
    const labels = ['', 'Very Weak', 'Weak', 'Moderate', 'Strong', 'Very Strong'];
    return labels[strength] || 'Unknown';
  };

  const handleEdit = (dependency: GoalDependency) => {
    setEditingDependency(dependency);
    setEditForm({
      dependencyType: dependency.dependencyType,
      strength: dependency.strength,
      description: dependency.description || '',
      impactPercentage: dependency.impactPercentage
    });
  };

  const handleSaveEdit = async () => {
    if (!editingDependency) return;

    try {
      setLoading(editingDependency.id);
      await apiService.updateDependency(editingDependency.id, {
        dependencyType: editForm.dependencyType,
        strength: editForm.strength,
        description: editForm.description,
        impactPercentage: editForm.impactPercentage
      });
      setEditingDependency(null);
      onUpdate();
    } catch (error) {
      console.error('Failed to update dependency:', error);
    } finally {
      setLoading(null);
    }
  };

  const handleDelete = async (dependency: GoalDependency) => {
    if (!window.confirm('Are you sure you want to delete this dependency?')) {
      return;
    }

    try {
      setLoading(dependency.id);
      await apiService.deleteDependency(dependency.id);
      onDelete();
    } catch (error) {
      console.error('Failed to delete dependency:', error);
    } finally {
      setLoading(null);
    }
  };

  const groupedDependencies = dependencies.reduce((acc, dep) => {
    const key = dep.dependencyType;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(dep);
    return acc;
  }, {} as Record<string, GoalDependency[]>);

  if (dependencies.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="text-center text-gray-500 py-12">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <ArrowRight className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Dependencies</h3>
          <p className="text-gray-500">
            Create dependencies between goals to see them listed here
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {Object.entries(groupedDependencies).map(([type, deps]) => (
        <div key={type} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className={`px-6 py-4 border-b border-gray-200 ${getDependencyColor(type)}`}>
            <div className="flex items-center gap-3">
              {getDependencyIcon(type)}
              <h3 className="text-lg font-semibold text-gray-900">
                {formatDependencyType(type)}
              </h3>
              <span className="px-2 py-1 bg-white rounded-full text-sm font-medium text-gray-600">
                {deps.length}
              </span>
            </div>
          </div>

          <div className="divide-y divide-gray-200">
            {deps.map((dependency) => (
              <div key={dependency.id} className="p-6">
                {editingDependency?.id === dependency.id ? (
                  // Edit form
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Dependency Type
                        </label>
                        <select
                          value={editForm.dependencyType}
                          onChange={(e) => setEditForm({ ...editForm, dependencyType: e.target.value as 'blocks' | 'enables' | 'reduces_complexity' | 'accelerates' })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="blocks">Blocks</option>
                          <option value="enables">Enables</option>
                          <option value="reduces_complexity">Reduces Complexity</option>
                          <option value="accelerates">Accelerates</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Strength (1-5)
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="5"
                          value={editForm.strength}
                          onChange={(e) => setEditForm({ ...editForm, strength: parseInt(e.target.value) })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Impact Percentage
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={editForm.impactPercentage}
                        onChange={(e) => setEditForm({ ...editForm, impactPercentage: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        value={editForm.description}
                        onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Optional description..."
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={handleSaveEdit}
                        disabled={loading === dependency.id}
                        className="btn-primary"
                      >
                        Save Changes
                      </button>
                      <button
                        onClick={() => setEditingDependency(null)}
                        className="btn-secondary"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  // Display view
                  <div>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="font-semibold text-gray-900">
                            {dependency.sourceGoal?.name || 'Unknown Goal'}
                          </div>
                          <ArrowRight className="w-4 h-4 text-gray-400" />
                          <div className="font-semibold text-gray-900">
                            {dependency.targetGoal?.name || 'Unknown Goal'}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                          <div className="flex items-center gap-1">
                            <span className="font-medium">Project:</span>
                            <span>{dependency.sourceGoal?.projectName || 'Unknown'}</span>
                            <ArrowRight className="w-3 h-3" />
                            <span>{dependency.targetGoal?.projectName || 'Unknown'}</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                          <div className="flex items-center gap-1">
                            <span className="font-medium">Strength:</span>
                            <span>{getStrengthText(dependency.strength)} ({dependency.strength}/5)</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="font-medium">Impact:</span>
                            <span>{dependency.impactPercentage}%</span>
                          </div>
                          {dependency.isCritical && (
                            <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full">
                              Critical
                            </span>
                          )}
                        </div>

                        {dependency.description && (
                          <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-md">
                            {dependency.description}
                          </p>
                        )}
                      </div>

                      <div className="flex items-center gap-2 ml-4">
                        <button
                          onClick={() => handleEdit(dependency)}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                          title="Edit dependency"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(dependency)}
                          disabled={loading === dependency.id}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                          title="Delete dependency"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Created: {new Date(dependency.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};